/**
 * A custom send: any combination of the seven sciences at once, rather than the
 * single-science counts the pane edits inline. Resolves to one mutagen figure,
 * which becomes the end of the selection.
 */

import { useEffect } from 'react';

import {
  SCIENCE,
  SCIENCE_ORDER,
  basketMutagen,
  formatCompact,
  formatInt,
  formatPercent,
  toDisplayMutagen,
  type FlaskBasket,
} from '../model';
import { NumberField } from './controls';

const EMPTY: FlaskBasket = {};

export function FlaskMixDialog({
  difficulty,
  basket,
  onBasketChange,
  onApply,
  onClose,
}: {
  difficulty: number;
  /** Owned by the caller so a mix survives closing and reopening the dialog. */
  basket: FlaskBasket;
  onBasketChange: (basket: FlaskBasket) => void;
  /** Raw mutagen carried by the basket. */
  onApply: (mutagen: number) => void;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const mutagen = basketMutagen(basket, difficulty);
  const total = SCIENCE_ORDER.reduce((sum, key) => sum + (basket[key] ?? 0), 0);

  return (
    <div className="dialog-backdrop" onPointerDown={onClose}>
      <div
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Custom flask mix"
        onPointerDown={(event) => event.stopPropagation()}
      >
        <header className="dialog-head">
          <h3>Custom flask mix</h3>
          <span className="dialog-hint">at {formatPercent(difficulty, 0)} difficulty</span>
        </header>

        <div className="mix-rows">
          {SCIENCE_ORDER.map((key) => {
            const count = basket[key] ?? 0;
            return (
              <div className="mix-row" key={key}>
                <span className="swatch" style={{ background: SCIENCE[key].hex }} />
                <span className="mix-name">{SCIENCE[key].colour}</span>
                <NumberField
                  label={`${SCIENCE[key].colour} flask count`}
                  value={count}
                  onChange={(next) => onBasketChange({ ...basket, [key]: next })}
                  min={0}
                  step={100}
                  wide
                />
                <span className="mix-mutagen">
                  {count > 0
                    ? formatCompact(toDisplayMutagen(SCIENCE[key].value * difficulty * count), 1)
                    : '–'}
                </span>
              </div>
            );
          })}
        </div>

        <footer className="dialog-foot">
          <span className="dialog-total">
            <span className="dialog-total-label">{formatInt(total)} flasks</span>
            <b>{formatCompact(toDisplayMutagen(mutagen), 1)}</b>
          </span>
          <div className="dialog-actions">
            <button type="button" className="ghost" onClick={() => onBasketChange(EMPTY)}>
              clear
            </button>
            <button type="button" onClick={onClose}>
              cancel
            </button>
            <button
              type="button"
              className="primary"
              disabled={mutagen <= 0}
              onClick={() => {
                onApply(mutagen);
                onClose();
              }}
            >
              select this send
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
